import { TextInput } from '@/components/ui/text-input';
import { useAuth } from '@/hooks/use-auth';
import { validateEmail } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { BlurView } from 'expo-blur';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { AuthScreenLayout } from './components/auth-screen-layout';

const logger = createLogger('FORGOT_PASSWORD');

export function ForgotPasswordScreen() {
  const { forgetPassword } = useAuth();

  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSendResetLink = async () => {
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await forgetPassword(email.trim());

      if (result.error) {
        logger.error('Forgot password failed:', result.error);
        setErrors({ email: result.error.message || 'Failed to send reset link. Please try again.' });
      } else {
        logger.log('Password reset email sent successfully');
        setIsSuccess(true);
      }
    } catch {
      setErrors({ email: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthScreenLayout title="Forgot Password">
      <BlurView intensity={50} className="rounded-xl overflow-hidden p-6" style={{ width: '100%' }}>
        {isSuccess ? (
          <View className="space-y-4">
            <Text className="text-xl font-semibold text-center mb-2 text-warm-gray-100">
              Check Your Email
            </Text>
            <Text className="text-base text-center text-warm-gray-100">
              We&apos;ve sent a password reset link to
            </Text>
            <Text className="text-base font-semibold text-center mb-4 text-warm-gray-100">
              {email}
            </Text>
            <Text className="text-sm text-center text-warm-gray-300">
              Please check your email and click the link to reset your password.
            </Text>
            <Pressable
              className="p-4 my-4 rounded-lg bg-primary"
              onPress={() => router.push({ pathname: '/(auth)/login' })}
            >
              <Text className="font-semibold text-center text-warm-gray-950">Back to Login</Text>
            </Pressable>
          </View>
        ) : (
          <View className="space-y-4">
            <Text className="text-xl font-semibold text-center mb-2 text-warm-gray-100">
              Forgot Password?
            </Text>
            <Text className="text-base text-center mb-4 text-warm-gray-100">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </Text>

            <TextInput
              placeholder="Email"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                if (errors.email) setErrors((prev) => ({ ...prev, email: '' }));
              }}
              autoCapitalize="none"
              keyboardType="email-address"
              editable={!isLoading}
              error={errors.email || undefined}
              containerClassName="my-1"
            />

            <Pressable
              className="p-4 my-4 rounded-lg bg-primary"
              onPress={handleSendResetLink}
              disabled={isLoading}
            >
              <Text className="font-semibold text-center text-warm-gray-950">
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Text>
            </Pressable>
          </View>
        )}
      </BlurView>

      {!isSuccess && (
        <View className="flex-row justify-center mt-6">
          <Pressable onPress={() => router.push({ pathname: '/(auth)/login' })}>
            <Text className="font-semibold underline text-warm-gray-100">Back to Login</Text>
          </Pressable>
        </View>
      )}
    </AuthScreenLayout>
  );
}
